import React, { useCallback, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { Play, Pause } from '@videoblocks/react-icons';

import {
  useVideoPlayback,
  useVideoSelection,
} from '../context/VideoEditorContext';
import { formatTimeDisplay } from '../utils/videoUtils';

interface VideoPlayerProps {
  src: string;
  isPlaying: boolean;
  onPlayPause: () => void;
  onTimeUpdate: (currentTime: number) => void;
  className?: string;
  currentTime?: number;
}

export default function VideoPlayer({
  src,
  isPlaying,
  onPlayPause,
  onTimeUpdate,
  className = '',
  currentTime,
}: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [videoDuration, setVideoDuration] = useState(0);
  const [displayTime, setDisplayTime] = useState(0);

  const { selection } = useVideoSelection();
  const { registerSeek } = useVideoPlayback();

  useEffect(() => {
    return registerSeek((time: number) => {
      const video = videoRef.current;
      if (!video || video.seeking) return;
      video.currentTime = time;
      setDisplayTime(time);
      onTimeUpdate(time);
    });
  }, [registerSeek, onTimeUpdate]);

  const handleLoadedMetadata = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      setVideoDuration(video.duration);
      setIsVideoReady(true);
    }
  }, []);

  const handleTimeUpdate = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      onTimeUpdate(video.currentTime);
      setDisplayTime(video.currentTime);

      if (selection && isPlaying && video.currentTime >= selection.endTime) {
        video.currentTime = selection.startTime;
        onTimeUpdate(video.currentTime);
        setDisplayTime(video.currentTime);
        video.play().catch(() => {});
      }
    }
  }, [onTimeUpdate, selection, isPlaying]);

  const handlePlay = useCallback(() => {
    const video = videoRef.current;
    if (video && selection) {
      if (
        video.currentTime >= selection.endTime ||
        video.currentTime < selection.startTime
      ) {
        video.currentTime = selection.startTime;
      }
    }
  }, [selection]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isVideoReady) return;

    if (isPlaying) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [isPlaying, isVideoReady]);

  // Seek to externally-set currentTime (from drag/trim operations)
  useEffect(() => {
    const video = videoRef.current;
    if (video && isVideoReady && currentTime !== undefined) {
      if (Math.abs(video.currentTime - currentTime) > 0.01) {
        video.currentTime = currentTime;
        setDisplayTime(currentTime);
      }
    }
  }, [currentTime, isVideoReady]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const video = videoRef.current;
      if (!video || !isVideoReady) return;

      switch (e.key) {
        case ' ':
        case 'Enter':
          e.preventDefault();
          onPlayPause();
          break;
      }
    },
    [onPlayPause, isVideoReady]
  );

  const handleContainerClick = useCallback(() => {
    onPlayPause();
  }, [onPlayPause]);

  return (
    <div
      className={twMerge(
        'relative flex justify-center focus:outline-none ',
        className
      )}
      style={{
        aspectRatio: '16/9',
      }}
      onClick={handleContainerClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label="Video player - press space to play/pause"
    >
      <video
        ref={videoRef}
        src={src}
        onLoadedMetadata={handleLoadedMetadata}
        onTimeUpdate={handleTimeUpdate}
        onPlay={handlePlay}
        className="object-cover rounded-t-lg"
        style={{
          border: '1.5px solid rgba(255, 255, 255, 0.20)',
        }}
        preload="metadata"
        muted
      />

      <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-black to-transparent pointer-events-none" />

      {isVideoReady && (
        <div className="absolute bottom-3 left-3 flex items-center gap-2 pointer-events-auto">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPlayPause();
            }}
            className="flex items-center justify-center hover:bg-white hover:bg-opacity-10 rounded p-1 transition-colors"
            aria-label={isPlaying ? 'Pause video' : 'Play video'}
          >
            {isPlaying ? (
              <Pause className="w-4 h-4 fill-white" />
            ) : (
              <Play className="w-4 h-4 fill-white" />
            )}
          </button>
          <div
            className="text-white text-sm font-sans"
            data-testid="video-time-display"
          >
            {formatTimeDisplay(displayTime)} /{' '}
            {formatTimeDisplay(videoDuration)}
          </div>
        </div>
      )}
    </div>
  );
}

VideoPlayer.displayName = 'VideoPlayer';
